import React, { useMemo } from 'react';

import Table from '@amzn/awsui-components-console/table';
import Header from '@amzn/awsui-components-console/header';
import Box from '@amzn/awsui-components-console/box';
import SpaceBetween from '@amzn/awsui-components-console/space-between';
import { useNavigate } from 'react-router';

import PaginationControl from 'src/common/ui/PaginationControl/PaginationControl';
import { HistoricalTranslationJob } from '../types';
import { TRACK_STRINGS } from '../constants';
import { createHistoricalColumnDefinitions } from './columns';

interface HistoricalJobsTableProps {
  items: HistoricalTranslationJob[];
  loading: boolean;
  currentPageIndex: number;
  pagesCount: number;
  hasMore: boolean;
  onPageChange: (pageIndex: number) => void;
}

const HistoricalJobsTable: React.FC<HistoricalJobsTableProps> = ({
  items,
  loading,
  currentPageIndex,
  pagesCount,
  hasMore,
  onPageChange,
}) => {
  const navigate = useNavigate();
  const columnDefinitions = useMemo(
    () => createHistoricalColumnDefinitions(navigate),
    [navigate],
  );

  return (
    <Table<HistoricalTranslationJob>
      variant="container"
      trackBy="trackingId"
      ariaLabels={{
        tableLabel: 'Historical translation jobs',
      }}
      stickyHeader
      stickyColumns={{ first: 1 }}
      stripedRows
      resizableColumns
      loading={loading}
      loadingText={TRACK_STRINGS.HISTORICAL_JOBS_LOADING}
      skeleton={loading ? { totalRows: 5 } : undefined}
      header={
        <Header variant="h2">
          {TRACK_STRINGS.HISTORICAL_JOBS_HEADER}{' '}
          <Box
            variant="span"
            fontSize="body-s"
            fontWeight="normal"
            color="text-body-secondary"
          >
            {TRACK_STRINGS.HISTORICAL_JOBS_SUBTITLE}
          </Box>
        </Header>
      }
      pagination={
        items.length > 0 ? (
          <PaginationControl
            currentPageIndex={currentPageIndex}
            pagesCount={pagesCount}
            openEnd={hasMore}
            onPageChange={onPageChange}
          />
        ) : undefined
      }
      columnDefinitions={columnDefinitions}
      items={items}
      empty={
        <Box textAlign="center" color="inherit">
          <SpaceBetween size="m">
            <Box variant="strong">
              {TRACK_STRINGS.HISTORICAL_JOBS_EMPTY_TITLE}
            </Box>
            <Box variant="p" color="inherit">
              {TRACK_STRINGS.HISTORICAL_JOBS_EMPTY_BODY}
            </Box>
          </SpaceBetween>
        </Box>
      }
    />
  );
};

export default HistoricalJobsTable;
